import CheckIcon from "@mui/icons-material/Check";
import ClearIcon from "@mui/icons-material/Clear";
import EditIcon from "@mui/icons-material/Edit";
import type { FormikValues } from "formik";
import { useField, useFormikContext } from "formik";
import { get as _get } from "lodash-es";
import type { ReactNode } from "react";
import React, { useCallback, useMemo, useRef, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import { ErrorOutlineIconWithTooltip } from "@citrine/atoms/error-outline-icon-with-tooltip";
import { IconButton } from "@citrine/atoms/icon-button";
import { TruncatedText } from "@citrine/atoms/truncated-text";
import type { Paths } from "@citrine/utils/types";

import { useFormConfiguration } from "./form";
import * as sx from "./integrated-range-field.sx";
import { isInputEnterEvent } from "./utils";

export interface IntegratedRangeFieldProps<
  Shape extends FormikValues = FormikValues,
> {
  className?: string;
  defaultEditing?: boolean;
  disabled?: boolean;
  maxLabel?: string;
  maxName: Paths<Shape, number>;
  minLabel?: string;
  minName: Paths<Shape, number>;
  onCancel?: () => void;
  onConfirm?: (lower: number, upper: number) => void;
  onEdit?: () => void;
  renderValue?: (lower: number, upper: number) => ReactNode;
  size?: "small" | "medium";
  units?: string;
}

export function IntegratedRangeField<
  Shape extends FormikValues = FormikValues,
>({
  className,
  defaultEditing = false,
  disabled,
  maxLabel,
  maxName,
  minLabel,
  minName,
  onCancel,
  onConfirm,
  onEdit,
  renderValue,
  size,
  units,
}: IntegratedRangeFieldProps<Shape>) {
  const { formatMessage } = useIntl();

  const { readonly, submitOnEnter } = useFormConfiguration();
  const { submitForm, validateForm } = useFormikContext();

  const [{ value: minValue }, { error: minError }, { setValue: setMin }] =
    useField<number>(minName);
  const [{ value: maxValue }, { error: maxError }, { setValue: setMax }] =
    useField<number>(maxName);

  const error = maxError || minError;

  const [isEditing, setIsEditing] = useState(defaultEditing);

  const prevMinValue = useRef<number>(minValue);
  const prevMaxValue = useRef<number>(maxValue);

  const cancel = useCallback(async () => {
    await setMin(prevMinValue.current);
    await setMax(prevMaxValue.current);
    setIsEditing(false);
    await onCancel?.();
  }, [onCancel, setMax, setMin]);

  const runFormValidation = useCallback(async () => {
    const validationErrors = await validateForm();
    const minErrors = _get(validationErrors, minName);
    const maxErrors = _get(validationErrors, maxName);
    if (!minErrors && !maxErrors) {
      setIsEditing(false);
    }

    return minErrors || maxErrors;
  }, [maxName, minName, setIsEditing, validateForm]);

  const confirm = useCallback(async () => {
    const errors = await runFormValidation();
    if (!errors) {
      onConfirm?.(minValue, maxValue);
      if (submitOnEnter) {
        await submitForm();
      }
    }
  }, [
    minValue,
    onConfirm,
    runFormValidation,
    submitForm,
    submitOnEnter,
    maxValue,
  ]);

  const onKeyDown = useCallback(
    async (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === "Escape") {
        e.preventDefault();
        cancel();
      } else if (isInputEnterEvent(e)) {
        e.preventDefault();
        await runFormValidation();
      }
    },
    [cancel, runFormValidation],
  );

  const renderedValue = useMemo(() => {
    const formattedValue = renderValue
      ? renderValue(minValue, maxValue)
      : `${minValue} - ${maxValue}`;
    return units ? `${formattedValue} ${units}` : formattedValue;
  }, [maxValue, minValue, renderValue, units]);

  const slotProps = useMemo(
    () => (units ? { input: { endAdornment: units } } : {}),
    [units],
  );

  return (
    <sx.Container className={className} isEditing={isEditing} size={size}>
      {isEditing ? (
        <sx.RangeInputs>
          <sx.TextField
            autoFocus
            fullWidth
            label={minLabel ?? formatMessage({ id: "min" })}
            name={minName}
            onKeyDown={onKeyDown}
            size={size}
            slotProps={slotProps}
            type="number"
          />
          <sx.TextField
            fullWidth
            label={maxLabel ?? formatMessage({ id: "max" })}
            name={maxName}
            onKeyDown={onKeyDown}
            size={size}
            slotProps={slotProps}
            type="number"
          />
          <sx.Actions active size={size}>
            <sx.SquareButton
              key="cancel"
              onClick={cancel}
              size={size}
              tooltip={formatMessage({ id: "cancel" })}
            >
              <ClearIcon fontSize="small" />
            </sx.SquareButton>
            <sx.ConfirmButton
              data-testid="submit-button"
              disabled={disabled}
              key="confirm"
              onClick={confirm}
              size={size}
              tooltip={formatMessage({ id: "Confirm" })}
              type="submit"
            >
              <CheckIcon fontSize="small" />
            </sx.ConfirmButton>
          </sx.Actions>
        </sx.RangeInputs>
      ) : (
        <>
          <sx.ValueContainer size={size}>
            <sx.Value asPlaceholder={!minValue && !maxValue}>
              <TruncatedText>{renderedValue}</TruncatedText>
            </sx.Value>
            {error ? (
              <ErrorOutlineIconWithTooltip
                error={<FormattedMessage id="Invalid range" />}
              />
            ) : null}
          </sx.ValueContainer>

          {readonly ? null : (
            <sx.Actions size={size}>
              <IconButton
                data-testid={`edit-${minName}-${maxName}-button`}
                key="edit"
                disabled={disabled}
                onClick={() => {
                  prevMinValue.current = minValue;
                  prevMaxValue.current = maxValue;
                  setIsEditing(true);
                  onEdit?.();
                }}
                size={size}
                tooltip={formatMessage({ id: "Edit" })}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </sx.Actions>
          )}
        </>
      )}
    </sx.Container>
  );
}
